/**
 * Приведение ответа ученика и эталона к одному виду перед сверкой.
 *
 * ⚠️ ЭТО БЛИЗНЕЦ `shared/textnorm/textnorm.go`. Не «похожий код», а именно
 * близнец: сервер ПЕРЕПРОВЕРЯЕТ ответ и заменяет вердикт браузера своим
 * (`progress-service`, `AnswerBlock`). Если две стороны разойдутся, ученик
 * увидит галочку на экране, а в базу уйдёт ошибка — это хуже исходного бага.
 *
 * Совпадение стережёт общий набор векторов
 * `shared/textnorm/testdata/vectors.json`, его читают обе стороны
 * (`normalizeAnswerMatch.test.ts`). Меняешь правило здесь — меняй в Go и
 * дописывай вектор.
 *
 * ⚠️ ЗАЧЕМ ВООБЩЕ. Телефонная клавиатура ставит U+2019 (’) вместо U+0027 (').
 * Ученик набирает «don’t», в ключе «don't» — на экране одно и то же, а ответ
 * помечался неверным, и догадаться было нельзя.
 */

/** Подстановка НАЧЕРТАНИЙ — по всей строке. Порядок пар повторяет Go. */
// ⚠️ Невидимые символы записаны escape-последовательностями НАМЕРЕННО, обратно
// их не возвращать: в исходнике они не видны при чтении, а линтер справедливо
// ругается на «irregular whitespace» и на склеенную последовательность в классе
// символов (потому ZWJ и вынесен в перечисление через |, а не в [...]).
const SYMBOLS: Array<[RegExp, string]> = [
  // апострофы: ’ ‘ ʼ ´ ` ′ → '
  [/[’‘ʼ´`′]/g, "'"],
  // кавычки: “ ” „ ‟ « » ″ → "
  [/[“”„‟«»″]/g, '"'],
  // тире: – — ‑ − → -
  [/[–—‑−]/g, '-'],
  // многоточие
  [/…/g, '...'],
  // пробелы, которые выглядят как обычный
  [/[\u00a0\u202f\u2007]/g, ' '],
  // ⚠️ НЕВИДИМЫЕ символы выбрасываем совсем. На стенде нашлись два пропуска,
  // где ключ начинался с U+200B: ответить на них не мог НИКТО, потому что
  // trim() такое не снимает, а увидеть глазами нельзя.
  [/\u200b|\u200c|\u200d|\ufeff/g, ''],
];

/** Знаки, снимаемые С КРАЁВ ответа. Внутри строки они значимы. */
const EDGE_PUNCT = /^[.,!?;:]+|[.,!?;:]+$/g;

/**
 * Начертание, регистр и пробелы — и больше ничего.
 *
 * Для ответов, которые ученик не набирает, а ВЫБИРАЕТ из данного списка:
 * перетаскивание слов, сопоставление, true/false.
 *
 * ⚠️ Живой пример, ради которого `fold` отделён от `normalizeAnswer`: в задании
 * «собери предложение» знак «?» — ОТДЕЛЬНАЯ карточка, которую ученик ставит в
 * конец. Сняв её как «краевую пунктуацию», мы засчитали бы предложение, где
 * «?» потерян или стоит не там.
 */
export function foldAnswer(s: string): string {
  return foldShapes((s ?? '').toLowerCase());
}

/** Начертания и пробелы, регистр — как есть (основа и `foldAnswer`, и сверки с регистром). */
function foldShapes(s: string): string {
  let out = s ?? '';
  for (const [re, to] of SYMBOLS) out = out.replace(re, to);
  return out.split(/\s+/).filter(Boolean).join(' ');
}

/**
 * `normalizeAnswer`, но РЕГИСТР СОХРАНЯЕТ: флаг автора «учитывать регистр»
 * (`case_sensitive`, решение владельца 13.10.2026). Близнец — `textnorm.AnswerCase`,
 * сторож — набор «answer_case» в `shared/textnorm/testdata/vectors.json`.
 */
export function normalizeAnswerCase(s: string): string {
  const folded = foldShapes(s);
  const trimmed = folded.replace(EDGE_PUNCT, '');
  return trimmed === '' ? folded : trimmed;
}

/**
 * То же, что `foldAnswer`, плюс снятие пунктуации по краям.
 *
 * Для ответов, которые ученик НАБИРАЕТ руками: пропуски, подпись к картинке.
 * Точка в конце там — про аккуратность набора, а не про знание языка.
 *
 * ⚠️ ПРЕДОХРАНИТЕЛЬ ПУСТОТЫ: если снятие обнуляет строку, возвращаем её до
 * снятия. Иначе ответ «?» станет пустым и совпадёт с любым другим таким же —
 * проверка молча превратится в «принимаю что угодно».
 */
export function normalizeAnswer(s: string): string {
  const folded = foldAnswer(s);
  const trimmed = folded.replace(EDGE_PUNCT, '');
  return trimmed === '' ? folded : trimmed;
}

/**
 * Совпал ли НАБРАННЫЙ ответ с эталоном.
 *
 * ⚠️ Две пустые строки НЕ равны: иначе пустой ответ проходил бы на заготовке
 * задания с пустым ключом, а таких в базе хватает.
 */
export function answersMatch(typed: string, correct: string): boolean {
  const a = normalizeAnswer(typed);
  return a !== '' && a === normalizeAnswer(correct);
}

/**
 * ВТОРАЯ ветка сверки набранного ответа урока: варианты через «/» в ключе.
 *
 * ⚠️ БЛИЗНЕЦ `services/progress-service/internal/service/slash_variants.go` (решения
 * владельца 12.09.2026, на проде). Сервер перепроверяет ответ и заменяет вердикт браузера
 * своим: пока фронт правила не знал, ученик набирал «traveled» на ключ
 * `/travelled/traveled`, видел «неверно» и эталон, а в базу уходило «верно».
 *
 * Правило: ключ и ответ делятся по «/» на части, у каждой — `normalizeAnswer`, пустые
 * выбрасываются. Засчитано, если в ответе есть хотя бы одна часть и КАЖДАЯ есть среди
 * частей ключа. Любой вариант, вся строка, обратный порядок и пробелы вокруг черты —
 * верно; чужой вариант в наборе и запятая вместо черты — нет.
 *
 * ⚠️ ДЕЛИТСЯ ВСЕГДА ЦЕЛИКОМ — это НЕ правило словаря (`wordVariants` в `wordAnswer.ts`,
 * D36): фраза «You're not Ashley/You aren't Ashley» по правилу словаря разрезалась бы на
 * бессмыслицу. Сводить два правила в одно нельзя.
 *
 * ⚠️ У КЛЮЧА БЕЗ «/» НИЧЕГО НЕ МЕНЯЕТСЯ: ответ «cat/cat» на ключ «cat» неверен.
 */
export function slashVariantMatch(typed: string, key: string, norm: (s: string) => string = normalizeAnswer): boolean {
  if (!(key ?? '').includes('/')) return false;
  const got = slashParts(typed, norm);
  if (got.length === 0) return false;
  const allowed = new Set(slashParts(key, norm));
  return got.every((g) => allowed.has(g));
}

function slashParts(s: string, norm: (s: string) => string): string[] {
  return (s ?? '')
    .split('/')
    .filter((p) => p.trim() !== '')
    .map(norm)
    .filter(Boolean);
}

/**
 * Сверка НАБРАННОГО ответа в блоках урока (`gap_fill`, `word_input_gap`, `word_input_image`):
 * прежнее равенство **или** варианты через «/».
 *
 * ⚠️ ТОЛЬКО РАСШИРЯЕТ. Прежнее сравнение идёт первым и дословно, как было, — не переносить
 * его внутрь `slashVariantMatch` «для краткости» (сторож — `lessonSlashVariants.test.ts`,
 * близнец `TestСлешВУрокеТолькоРасширяет`).
 */
export function lessonAnswerMatch(typed: string, key: string, caseSensitive = false): boolean {
  if (caseSensitive) {
    // Флаг автора «учитывать регистр» (13.10.2026): то же правило, регистр сохранён.
    // Близнец — typedMatch в progress-service block_grader.go.
    const a = normalizeAnswerCase(typed);
    return (a !== '' && a === normalizeAnswerCase(key)) || slashVariantMatch(typed, key, normalizeAnswerCase);
  }
  return answersMatch(typed, key) || slashVariantMatch(typed, key);
}

/** То же для ВЫБРАННЫХ, а не набранных ответов (см. `foldAnswer`). */
export function choicesMatch(picked: string, correct: string): boolean {
  const a = foldAnswer(picked);
  return a !== '' && a === foldAnswer(correct);
}
